import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { formatAndel, formatDager } from './api'
import type { StaatidInnsiktResponse, StaatidInnsiktSegment } from './api'

interface Props {
  data: StaatidInnsiktResponse | null
  loading: boolean
  onOpenTrail: (sluttSvvId: number) => void
}

// Translation keys, resolved at render (#714).
const KANAL_NAVN = {
  innbytte: 'innsikt.kanal.innbytte',
  leasing: 'innsikt.kanal.leasing',
  annet: 'innsikt.kanal.annet',
} as const satisfies Record<StaatidInnsiktSegment['kanal'], string>
const LEDD_NAVN = {
  direkte: 'innsikt.ledd.direkte',
  via: 'innsikt.ledd.via',
} as const satisfies Record<StaatidInnsiktSegment['ledd'], string>

const TH = 'px-4 py-2 font-medium text-gray-600 dark:text-gray-400'

/** "+18 d (+39 %)" / "−6 d (−12 %)". */
function formatDiff(s: StaatidInnsiktSegment): string {
  if (s.diff_dager === null) return '—'
  const sign = (v: number) => (v > 0 ? '+' : v < 0 ? '−' : '')
  const dager = `${sign(s.diff_dager)}${formatDager(Math.abs(s.diff_dager))} d`
  if (s.diff_andel === null) return dager
  return `${dager} (${sign(s.diff_andel)}${Math.round(Math.abs(s.diff_andel) * 100)} %)`
}

/** "Leasingretur, direkte" — the segment as one phrase. */
function segmentNavn(s: StaatidInnsiktSegment, t: TFunction<'staatid'>): string {
  return `${t(KANAL_NAVN[s.kanal])}, ${t(LEDD_NAVN[s.ledd]).toLowerCase()}`
}

/**
 * Innsikt (#662): where this dealer is slow against the market, segment by
 * segment (purchase channel x direct/via another dealer), and which cars.
 * Kommisjonssalg (0 days) are outside every segment figure and get their own
 * line.
 */
export default function InnsiktPanel({ data, loading, onOpenTrail }: Props) {
  const { t } = useTranslation('staatid')
  if (loading || !data) {
    return (
      <div className="card p-6 text-sm text-gray-500 dark:text-gray-400" data-testid="staatid-innsikt">
        {t('innsikt.loading')}
      </div>
    )
  }
  if (data.antall_roundtrips === 0) {
    return (
      <div className="card p-6 text-sm text-gray-500 dark:text-gray-400" data-testid="staatid-innsikt">
        <p data-testid="staatid-innsikt-tom">{t('innsikt.tom')}</p>
      </div>
    )
  }

  const potensial = data.storst_potensial
  return (
    <div className="space-y-4" data-testid="staatid-innsikt">
      <div className="card p-4 space-y-2 text-sm text-gray-700 dark:text-gray-300">
        <p data-testid="staatid-innsikt-potensial">
          {potensial ? (
            <>
              <span className="text-gray-500 dark:text-gray-400">{t('innsikt.potensialLabel')}</span>{' '}
              <strong className="text-amber-700 dark:text-amber-400">
                {t('innsikt.potensialValue', { segment: segmentNavn(potensial, t), dager: formatDager(potensial.diff_dager) })}
              </strong>
            </>
          ) : (
            t('innsikt.ingenPotensial')
          )}
        </p>
        <p data-testid="staatid-innsikt-kommisjon">
          <span className="text-gray-500 dark:text-gray-400">{t('common.stat.kommisjonssalg')}</span>{' '}
          <strong>{data.antall_kommisjon.toLocaleString('nb-NO')} ({formatAndel(data.andel_kommisjon)})</strong>
          <span className="text-gray-500 dark:text-gray-400"> {t('innsikt.kommisjonNote')}</span>
        </p>
      </div>

      <div className="card overflow-x-auto">
        <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('innsikt.segmentTitle')}</span>
        </div>
        <table className="w-full text-sm" data-testid="staatid-innsikt-segmenter">
          <thead>
            <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
              <th className={`${TH} text-left`}>{t('innsikt.kanalHeader')}</th>
              <th className={`${TH} text-left`}>{t('innsikt.leddHeader')}</th>
              <th className={`${TH} text-right`}>{t('common.antall')}</th>
              <th className={`${TH} text-right`}>{t('innsikt.medianForhandler')}</th>
              <th className={`${TH} text-right`}>{t('innsikt.medianMarked')}</th>
              <th
                className={`${TH} text-right`}
                title={t('innsikt.differanseTitle')}
              >
                {t('innsikt.differanse')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.segmenter.map(s => (
              <tr
                key={`${s.kanal}-${s.ledd}`}
                data-testid={`staatid-innsikt-segment-${s.kanal}-${s.ledd}`}
                data-flagget={s.flagget ? 'true' : 'false'}
                className={`border-b border-gray-100 dark:border-dark-border ${
                  s.flagget ? 'bg-amber-50 dark:bg-amber-900/30' : ''
                }`}
              >
                <td className="px-4 py-2">
                  <span className="inline-flex items-center gap-1.5">
                    {s.flagget && (
                      <AlertTriangle
                        className="w-4 h-4 text-amber-600 dark:text-amber-400"
                        aria-label={t('innsikt.flagg')}
                        data-testid="staatid-innsikt-flagg"
                      />
                    )}
                    {t(KANAL_NAVN[s.kanal])}
                  </span>
                </td>
                <td className="px-4 py-2">{t(LEDD_NAVN[s.ledd])}</td>
                <td className="px-4 py-2 text-right tabular-nums">{s.antall.toLocaleString('nb-NO')}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {s.for_faa_salg
                    ? <span className="text-gray-400 dark:text-gray-500 italic">{t('innsikt.forFaaSalg')}</span>
                    : `${formatDager(s.median_dager)} d`}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {s.marked_median_dager === null ? '—' : `${formatDager(s.marked_median_dager)} d`}
                </td>
                <td className={`px-4 py-2 text-right tabular-nums ${s.flagget ? 'font-semibold text-amber-700 dark:text-amber-400' : ''}`}>
                  {s.for_faa_salg ? '—' : formatDiff(s)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card overflow-x-auto">
        <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('innsikt.uteliggereTitle')}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="staatid-innsikt-uteliggere-antall">
            {data.antall_uteliggere > data.uteliggere.length
              ? t('innsikt.uteliggereShowing', { shown: data.uteliggere.length, total: data.antall_uteliggere.toLocaleString('nb-NO') })
              : t('innsikt.uteliggereCount', { total: data.antall_uteliggere.toLocaleString('nb-NO') })}
          </span>
        </div>
        <table className="w-full text-sm" data-testid="staatid-innsikt-uteliggere">
          <thead>
            <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
              <th className={`${TH} text-left`}>{t('innsikt.merkeModell')}</th>
              <th className={`${TH} text-right`}>{t('innsikt.alder')}</th>
              <th className={`${TH} text-left`}>{t('innsikt.drivstoff')}</th>
              <th className={`${TH} text-left`}>{t('innsikt.kanalHeader')}</th>
              <th className={`${TH} text-left`}>{t('innsikt.leddHeader')}</th>
              <th className={`${TH} text-right`}>{t('innsikt.dager')}</th>
              <th
                className={`${TH} text-right`}
                title={t('innsikt.p90Title')}
              >
                {t('innsikt.p90')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.uteliggere.map((u, i) => (
              <tr
                key={u.slutt_svv_id}
                data-testid="staatid-innsikt-uteligger"
                onClick={() => onOpenTrail(u.slutt_svv_id)}
                className={`border-b border-gray-100 dark:border-dark-border cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${
                  i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''
                }`}
              >
                <td className="px-4 py-2">{[u.merke, u.modell].filter(Boolean).join(' ') || '—'}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {u.bil_alder_ar === null ? '—' : t('innsikt.alderAar', { n: formatDager(u.bil_alder_ar) })}
                </td>
                <td className="px-4 py-2">{u.drivstoff ?? '—'}</td>
                <td className="px-4 py-2">{t(KANAL_NAVN[u.kanal])}</td>
                <td className="px-4 py-2">{t(LEDD_NAVN[u.ledd])}</td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold">{u.staatid_dager.toLocaleString('nb-NO')}</td>
                <td className="px-4 py-2 text-right tabular-nums">{u.marked_p90_dager.toLocaleString('nb-NO')}</td>
              </tr>
            ))}
            {data.uteliggere.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  {t('innsikt.ingenUteliggere')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
